import React, { useState, useEffect } from "react";
import { Plus, Download } from "lucide-react";
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { addTimelineEvent } from "../services/timelineService";
import ExpensesList from "../components/ExpensesList";
import AddExpenseForm from "../components/AddExpenseForm";
import { getStatusBucket } from "../utils/caseStatus";

function ExpensesTab({ caseId, caseData }) {
  const { globalUser } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState(null);
  const [userName, setUserName] = useState("");

  useEffect(() => {
    fetchUserName();
    fetchExpenses();
  }, [caseId, globalUser]);

  const fetchUserName = async () => {
    if (!globalUser?.uid) return;

    try {
      const userQuery = query(
        collection(db, "lawyers"),
        where("uid", "==", globalUser.uid)
      );
      const userSnapshot = await getDocs(userQuery);
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        setUserName(userData.name || "User");
      }
    } catch (error) {
      console.error("Error fetching user name:", error);
      setUserName("User");
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      // Try to get from pending collection first
      const pendingCaseDoc = doc(db, "pending", caseId);
      const pendingCaseSnapshot = await getDoc(pendingCaseDoc);

      let data;
      if (pendingCaseSnapshot.exists()) {
        data = pendingCaseSnapshot.data();
      } else {
        // If not in pending, check disposed collection
        const disposedCaseDoc = doc(db, "disposed", caseId);
        const disposedCaseSnapshot = await getDoc(disposedCaseDoc);
        if (disposedCaseSnapshot.exists()) {
          data = disposedCaseSnapshot.data();
        }
      }

      if (data && data.expenses) {
        setExpenses(data.expenses);
      } else {
        setExpenses([]);
      }
    } catch (error) {
      console.error("Error fetching expenses:", error);
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (newExpense) => {
    try {
      const expenseWithUser = {
        ...newExpense,
        userName: userName,
      };
      const updatedExpenses = [...expenses, expenseWithUser];
      setExpenses(updatedExpenses);

      // Save to Firebase
      await saveExpensesToFirebase(updatedExpenses);

      // Log timeline event
      const caseCol = getStatusBucket(caseData?.status);
      addTimelineEvent(caseId, caseCol, {
        type: "expense_added",
        title: "Expense added",
        description: `${newExpense.type || "Expense"}: ₹${newExpense.amount || 0}`,
        performedBy: globalUser?.uid || "",
        performedByName: userName || "",
        changes: {
          "Expense Type": { old: "—", new: newExpense.type || "—" },
          "Amount": { old: "—", new: `₹${newExpense.amount || 0}` },
          "Date": { old: "—", new: newExpense.date || "—" },
          "Description": { old: "—", new: newExpense.description || "—" },
          "Billable": { old: "—", new: newExpense.billable ? "Yes" : "No" },
        },
      });

      toast.success("Expense added successfully");
    } catch (error) {
      console.error("Error adding expense:", error);
      toast.error("Failed to add expense");
    }
  };

  const handleEditExpense = async (updatedExpense) => {
    try {
      const updatedExpenses = expenses.map((expense) =>
        expense.id === updatedExpense.id ? updatedExpense : expense
      );
      setExpenses(updatedExpenses);

      // Save to Firebase
      await saveExpensesToFirebase(updatedExpenses);
      setEditMode(false);
      setExpenseToEdit(null);
    } catch (error) {
      console.error("Error updating expense:", error);
      toast.error("Failed to update expense");
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      const updatedExpenses = expenses.filter(
        (expense) => (expense.id || expenses.indexOf(expense)) !== expenseId
      );
      setExpenses(updatedExpenses);

      // Save to Firebase
      await saveExpensesToFirebase(updatedExpenses);
      toast.success("Expense deleted successfully");
    } catch (error) {
      console.error("Error deleting expense:", error);
      toast.error("Failed to delete expense");
    }
  };

  const handleOpenEditModal = (expense) => {
    setExpenseToEdit(expense);
    setEditMode(true);
    setIsFormOpen(true);
  };

  const handleCloseModal = () => {
    setIsFormOpen(false);
    setEditMode(false);
    setExpenseToEdit(null);
  };

  const saveExpensesToFirebase = async (expensesToSave) => {
    try {
      // Try pending collection first
      const pendingDocRef = doc(db, "pending", caseId);
      const pendingDocSnap = await getDoc(pendingDocRef);

      if (pendingDocSnap.exists()) {
        await updateDoc(pendingDocRef, {
          expenses: expensesToSave
        });
      } else {
        // If not in pending, try disposed collection
        const disposedDocRef = doc(db, "disposed", caseId);
        const disposedDocSnap = await getDoc(disposedDocRef);

        if (disposedDocSnap.exists()) {
          await updateDoc(disposedDocRef, {
            expenses: expensesToSave
          });
        } else {
          throw new Error("Case not found in any collection");
        }
      }
    } catch (error) {
      console.error("Error saving expenses to Firebase:", error);
      throw error;
    }
  };

  const handleExportExpenses = () => {
    if (expenses.length === 0) {
      toast.error("No expenses to export");
      return;
    }

    try {
      // Create CSV content
      const headers = ["Date", "Type", "Description", "Amount", "Billable"];
      const csvContent = [
        headers.join(","),
        ...expenses.map((expense) =>
          [
            expense.date,
            `"${expense.type}"`,
            `"${expense.description || ""}"`,
            expense.amount,
            expense.billable ? "Billable" : "Non-Billable"
          ].join(",")
        )
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `expenses_${caseData?.caseNumber || "case"}_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Expenses exported successfully");
    } catch (error) {
      console.error("Error exporting expenses:", error);
      toast.error("Failed to export expenses");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C99F4A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Export + Add Expense buttons (same line) */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Case Expenses</h2>
        <div className="flex items-center gap-2">
          {expenses.length > 0 && (
            <button
              onClick={handleExportExpenses}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              <Download size={18} />
              Export
            </button>
          )}
          <button
            onClick={() => {
              setEditMode(false);
              setExpenseToEdit(null);
              setIsFormOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors"
          >
            <Plus size={20} />
            Add Expense
          </button>
        </div>
      </div>

      {/* Expenses List */}
      <ExpensesList
        expenses={expenses}
        onDelete={handleDeleteExpense}
        onEdit={handleOpenEditModal}
      />

      {/* Add/Edit Expense Form Modal */}
      <AddExpenseForm
        isOpen={isFormOpen}
        onClose={handleCloseModal}
        onAddExpense={handleAddExpense}
        editMode={editMode}
        expenseToEdit={expenseToEdit}
        onEditExpense={handleEditExpense}
      />
    </div>
  );
}

export default ExpensesTab;
